import { CircleAlert } from 'lucide-react';
import React from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@crossnokaye/ux-common';

import { Icon } from '@/ux-common/components/icon';

interface DiscardChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  /** Dialog title. Defaults to "Discard Unapplied Changes?" */
  title?: string;
  /** Dialog description. Accepts ReactNode for rich content (e.g. lists). */
  description?: React.ReactNode;
  /** Cancel button label. Defaults to "NO, KEEP EDITING" */
  cancelLabel?: string;
  /** Confirm button label. Defaults to "YES, DISCARD CHANGES" */
  confirmLabel?: string;
}

/**
 * DiscardChangesDialog
 *
 * Reusable confirmation dialog for destructive discard actions.
 * Supports custom title, description, and button labels via optional props.
 * Defaults match the original bulk-edit discard behavior.
 * Uses the AlertDialog component from @crossnokaye/ux-common to match design (Alert icon and destructive button)
 */
export const DiscardChangesDialog = ({
  open,
  onOpenChange,
  onConfirm,
  title = 'Discard Unapplied Changes?',
  description = 'You have unsaved changes that will be lost.',
  cancelLabel = 'NO, KEEP EDITING',
  confirmLabel = 'YES, DISCARD CHANGES',
}: DiscardChangesDialogProps): React.ReactNode => {
  const handleConfirm = async () => {
    await onConfirm();
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <div className="flex items-center gap-4 text-foreground">
              <Icon className="icon-sm icon-critical">
                <CircleAlert className="glyph glyph-sm" aria-hidden="true" />
              </Icon>
              {title}
            </div>
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div>{description}</div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={handleConfirm}>
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
